<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useTenant } from '@/composables/useTenant';
import { registerTenantAndManager, setAuthToken, type TenantRegistrationPayload } from '@/services/api';
import { clerkService } from '@/services/clerkService';
import { useToastNotification } from '@/composables/useToastNotification';
import type { TenantTier } from '@/types/models';
import SocialAuthButtons from '@/components/auth/SocialAuthButtons.vue';
import { NInput, NButton } from 'naive-ui';
import {
  Building2,
  User,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  KeyRound,
  RotateCcw,
  Globe2,
  AlertCircle
} from 'lucide-vue-next';

const router = useRouter();
const { t } = useI18n();
const { createTenant, setPersona, refreshData } = useTenant();
const { notifySuccess, notifyError, notifyInfo } = useToastNotification();

const currentStep = ref(1);
const isSubmitting = ref(false);
const isVerifying = ref(false);
const isResending = ref(false);

// Email verification state (when Clerk status is "missing_requirements")
const showVerificationStep = ref(false);
const verificationCode = ref('');
const verificationError = ref<string | null>(null);
const clerkUserId = ref<string | null>(null);

// Form Fields: Step 1 - Tenant Manager (User Account)
const managerForm = ref({
  fullName: '',
  email: '',
  phone: '',
  password: '',
  roleTitle: 'Club Director / Org Admin',
  socialProvider: null as string | null,
});

// Form Fields: Step 2 - Baseball Organization (Self-Managed Multi-Tenancy)
// Strictly: orgName, tier, sportType, seasonYear. DO NOT send orgId or slug to backend.
const tenantForm = ref({
  orgName: '',
  tier: 'COLLEGIATE' as TenantTier,
  sportType: 'baseball',
  seasonYear: '2025 Spring',
});

const tenantTierOptions: { label: string; value: TenantTier; desc: string }[] = [
  { label: '都市対抗・社会人 (PRO)', value: 'PRO', desc: '都市対抗野球、JABA社会人企業・クラブチーム、独立リーグ' },
  { label: '大学野球連盟 (COLLEGIATE)', value: 'COLLEGIATE', desc: '東京六大学、東都大学野球、各地区大学野球連盟' },
  { label: 'リトルシニア・中学硬式 (AMATEUR)', value: 'AMATEUR', desc: 'リトルシニア、ボーイズ、ヤング、ポニーリーグ' },
  { label: 'リトルリーグ・学童硬式 (ACADEMIC)', value: 'ACADEMIC', desc: '日本リトルリーグ協会、少年・学童硬式野球アカデミー' },
];

const handleSocialAuth = (provider: 'google' | 'apple' | 'line', profile?: { name: string; email: string }) => {
  if (profile) {
    managerForm.value.fullName = profile.name;
    managerForm.value.email = profile.email;
    managerForm.value.password = `clerk_oauth_${provider}_sec`;
    managerForm.value.socialProvider = provider;
    notifyInfo(
      'Manager Profile Synced',
      `Auto-filled credentials from ${provider.toUpperCase()}`
    );
  }
};

const handleNextStep = async () => {
  if (currentStep.value === 1) {
    if (!managerForm.value.fullName || !managerForm.value.email || !managerForm.value.password) {
      notifyError('Missing Fields', 'Please complete all required manager credentials.');
      return;
    }
    if (managerForm.value.password.length < 15) {
      notifyError('Password Policy', 'Clerk instance requires passwords to be at least 15 characters.');
      return;
    }

    isSubmitting.value = true;
    verificationError.value = null;

    try {
      const nameParts = managerForm.value.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || 'Manager';
      const lastName = nameParts.slice(1).join(' ') || '';

      notifyInfo('Clerk Auth', 'Creating user credentials on Clerk (Auth strictly)...');
      const clerkResult = await clerkService.signUp({
        emailAddress: managerForm.value.email,
        password: managerForm.value.password,
        firstName,
        lastName,
      });

      if (clerkResult.requiresVerification || clerkResult.status === 'missing_requirements') {
        clerkUserId.value = clerkResult.userId || null;
        showVerificationStep.value = true;
        notifyInfo('Verification Required', `A 6-digit verification code has been sent to ${managerForm.value.email}`);
        return;
      }

      if (!clerkResult.success) {
        notifyError('Clerk Signup Failed', clerkResult.error || 'Registration failed on Clerk.');
        return;
      }

      if (clerkResult.userId) {
        clerkUserId.value = clerkResult.userId;
      }
      if (clerkResult.token) {
        setAuthToken(clerkResult.token);
      }
      currentStep.value = 2;
    } catch (err: any) {
      notifyError('Clerk Signup Error', err?.message || 'Failed to submit registration to Clerk.');
    } finally {
      isSubmitting.value = false;
    }
  }
};

const handleVerifyCode = async () => {
  if (!verificationCode.value || verificationCode.value.trim().length < 4) {
    verificationError.value = 'Please enter the verification code sent to your email.';
    return;
  }

  isVerifying.value = true;
  verificationError.value = null;

  try {
    notifyInfo('Verifying Code', 'Submitting code to Clerk...');
    const result = await clerkService.verifyEmailCode(verificationCode.value.trim());

    if (!result.success) {
      verificationError.value = result.error || 'Invalid verification code. Please check your email and try again.';
      notifyError('Verification Failed', verificationError.value);
      return;
    }

    if (result.userId) {
      clerkUserId.value = result.userId;
    }
    if (result.token) {
      setAuthToken(result.token);
    }

    showVerificationStep.value = false;
    notifySuccess('Email Verified', 'Your email address has been verified with Clerk.');
    currentStep.value = 2;
  } catch (err: any) {
    verificationError.value = err?.message || 'Verification attempt failed.';
    notifyError('Verification Error', verificationError.value);
  } finally {
    isVerifying.value = false;
  }
};

const handleResendCode = async () => {
  isResending.value = true;
  try {
    const res = await clerkService.resendVerificationCode();
    if (res.success) {
      notifySuccess('Code Resent', 'A new verification code has been sent to your email.');
    } else {
      notifyError('Resend Failed', res.message || 'Could not resend code.');
    }
  } catch (err: any) {
    notifyError('Resend Error', err.message || 'Failed to resend verification code.');
  } finally {
    isResending.value = false;
  }
};

const handleRegisterTenant = async () => {
  if (!tenantForm.value.orgName) {
    notifyError('Missing Information', 'Please provide a valid baseball organization name.');
    return;
  }

  isSubmitting.value = true;

  try {
    const effectiveUserId = clerkUserId.value || `user_clerk_${Date.now().toString(36)}`;

    // Spec compliance: POST /api/v1/tenants/register
    // Form fields: orgName, tier, sportType, managerName, managerEmail, managerPhone.
    // DO NOT send orgId or slug fields to the backend!
    const syncPayload: TenantRegistrationPayload = {
      userId: effectiveUserId,
      orgName: tenantForm.value.orgName.trim(),
      tier: tenantForm.value.tier,
      sportType: tenantForm.value.sportType || 'baseball',
      seasonYear: tenantForm.value.seasonYear,
      managerName: managerForm.value.fullName.trim(),
      managerEmail: managerForm.value.email.trim(),
      managerPhone: managerForm.value.phone ? managerForm.value.phone.trim() : undefined,
    };

    console.info('[TenantRegistration] Calling POST /api/v1/tenants/register with spec-conforming payload:', syncPayload);
    const apiResponse = await registerTenantAndManager(syncPayload);

    const serverTenant = apiResponse?.data?.tenant || apiResponse?.data || apiResponse?.tenant;
    const finalTenantId = serverTenant?.id || `${Date.now()}-tenant-uuid`;
    const finalOrgName = serverTenant?.name || tenantForm.value.orgName;
    const finalTier = serverTenant?.tier || tenantForm.value.tier;

    // Create and switch active tenant in self-managed multi-tenant state
    const createdTenant = createTenant({
      id: finalTenantId,
      name: finalOrgName,
      tier: finalTier,
      type: finalTier,
      sportType: tenantForm.value.sportType,
      seasonYear: tenantForm.value.seasonYear,
    });

    // Set persona to ADMIN
    setPersona({
      id: effectiveUserId,
      fullName: managerForm.value.fullName,
      email: managerForm.value.email,
      role: 'ADMIN',
      avatarUrl: `https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80`,
    });

    if (typeof window !== 'undefined') {
      localStorage.setItem('nbc_tenant_owner_' + managerForm.value.email, createdTenant.id);
    }

    await refreshData();

    notifySuccess(
      t('tenantReg.successTitle'),
      t('tenantReg.successMsg', { org: createdTenant.name, user: managerForm.value.fullName })
    );

    notifyInfo(
      'Service Activation',
      'Select and activate service modules to unlock your baseball operations suite.'
    );

    router.push('/dashboard/billing');
  } catch (err: any) {
    notifyError('Registration Error', err?.message || 'Failed to complete tenant setup.');
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-bold mb-3">
        <Sparkles class="w-3.5 h-3.5" />
        Self-Managed Multi-Tenancy Registration
      </div>
      <h1 class="text-2xl sm:text-3xl font-black text-slate-100 tracking-tight">
        {{ t('tenantReg.pageTitle') }}
      </h1>
      <p class="mt-2 text-xs sm:text-sm text-slate-400 max-w-md mx-auto">
        {{ t('tenantReg.pageSubtitle') }}
      </p>
    </div>

    <!-- Step Progress Indicator -->
    <div class="p-4 bg-slate-900 border border-slate-800/80 rounded-2xl">
      <div class="grid grid-cols-2 gap-2">
        <div
          :class="[
            'p-2.5 rounded-xl border flex items-center gap-2.5 transition',
            currentStep === 1
              ? 'bg-sky-500/10 border-sky-500/40 text-sky-400'
              : 'bg-slate-950 border-slate-800 text-slate-400'
          ]"
        >
          <div :class="['w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0', currentStep === 1 ? 'bg-sky-500 text-slate-950' : 'bg-slate-800 text-slate-400']">
            1
          </div>
          <div class="min-w-0">
            <p class="text-xs font-bold truncate">{{ t('tenantReg.step1Label') }}</p>
          </div>
        </div>

        <div
          :class="[
            'p-2.5 rounded-xl border flex items-center gap-2.5 transition',
            currentStep === 2
              ? 'bg-sky-500/10 border-sky-500/40 text-sky-400'
              : 'bg-slate-950 border-slate-800 text-slate-400'
          ]"
        >
          <div :class="['w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0', currentStep === 2 ? 'bg-sky-500 text-slate-950' : 'bg-slate-800 text-slate-400']">
            2
          </div>
          <div class="min-w-0">
            <p class="text-xs font-bold truncate">{{ t('tenantReg.step2Label') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- PENDING EMAIL VERIFICATION MODAL / STEP -->
    <div v-if="showVerificationStep" class="p-5 bg-slate-900 border border-sky-500/40 rounded-2xl space-y-4 shadow-xl">
      <div class="flex items-start gap-3">
        <div class="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
          <KeyRound class="w-5 h-5" />
        </div>
        <div>
          <h3 class="text-sm font-bold text-slate-100">Clerk Email Verification Required</h3>
          <p class="text-xs text-slate-400 mt-0.5">
            Please enter the 6-digit verification code sent to your email:
          </p>
          <p class="text-xs font-mono font-bold text-sky-400 mt-1">{{ managerForm.email }}</p>
        </div>
      </div>

      <div v-if="verificationError" class="p-3 bg-rose-500/10 border border-rose-500/30 rounded-xl text-xs text-rose-300 flex items-start gap-2">
        <AlertCircle class="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
        <span>{{ verificationError }}</span>
      </div>

      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.enterCodeLabel') }}</label>
        <NInput
          v-model:value="verificationCode"
          placeholder="e.g. 123456"
          size="large"
          class="font-mono text-center tracking-widest text-lg"
          @keyup.enter="handleVerifyCode"
          autofocus
        />
      </div>

      <div class="pt-2 flex flex-col sm:flex-row items-center justify-between gap-2">
        <button
          type="button"
          @click="handleResendCode"
          :disabled="isResending"
          class="text-xs font-semibold text-sky-400 hover:text-sky-300 flex items-center gap-1.5 transition disabled:opacity-50"
        >
          <RotateCcw :class="['w-3.5 h-3.5', isResending ? 'animate-spin' : '']" />
          <span>{{ isResending ? t('tenantReg.resendingCode') : t('tenantReg.resendCode') }}</span>
        </button>

        <div class="flex items-center gap-2 w-full sm:w-auto justify-end">
          <button
            type="button"
            @click="showVerificationStep = false"
            class="px-3 py-2 rounded-xl text-xs text-slate-400 hover:text-slate-200"
          >
            {{ t('tenantReg.editEmail') }}
          </button>
          <NButton
            type="primary"
            :loading="isVerifying"
            @click="handleVerifyCode"
            class="font-bold text-xs"
          >
            {{ t('tenantReg.verifyContinue') }}
          </NButton>
        </div>
      </div>
    </div>

    <!-- STEP 1: Manager Information (Clerk User Sign-Up) -->
    <form v-else-if="currentStep === 1" @submit.prevent="handleNextStep" class="space-y-4">
      <div class="p-3 bg-sky-500/10 border border-sky-500/20 rounded-2xl flex items-center gap-3">
        <div class="w-8 h-8 rounded-xl bg-sky-500/20 text-sky-400 flex items-center justify-center shrink-0">
          <User class="w-4 h-4" />
        </div>
        <p class="text-xs text-sky-200">
          {{ t('tenantReg.managerTip') }}
        </p>
      </div>

      <!-- Quick Social Auth Sync for Manager -->
      <div>
        <p class="text-[11px] font-semibold text-slate-400 mb-2">{{ t('tenantReg.autoFillSocial') }}</p>
        <SocialAuthButtons mode="register" @social-auth="handleSocialAuth" />
      </div>

      <div class="relative flex py-1 items-center">
        <div class="flex-grow border-t border-slate-800"></div>
        <span class="flex-shrink mx-3 text-[10px] text-slate-500 uppercase font-bold tracking-wider">{{ t('tenantReg.orEnterCredentials') }}</span>
        <div class="flex-grow border-t border-slate-800"></div>
      </div>

      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.fullName') }} *</label>
        <NInput
          v-model:value="managerForm.fullName"
          placeholder="例: 高橋 健二 (Kenji Takahashi)"
          size="medium"
          required
        />
      </div>

      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.workEmail') }} *</label>
        <NInput
          v-model:value="managerForm.email"
          type="text"
          placeholder="k.takahashi@nextbase.co.jp"
          size="medium"
          required
        />
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.phoneOptional') }}</label>
          <NInput
            v-model:value="managerForm.phone"
            placeholder="090-1234-5678"
            size="medium"
          />
        </div>

        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.password') }} *</label>
          <NInput
            v-model:value="managerForm.password"
            type="password"
            show-password-on="click"
            placeholder="Min 15 characters"
            size="medium"
            required
          />
          <p class="text-[10px] text-slate-400 mt-1">{{ t('tenantReg.passwordPolicyNote') }}</p>
        </div>
      </div>

      <div class="pt-3 flex items-center justify-between gap-2">
        <router-link
          to="/auth/login"
          class="text-xs font-semibold text-sky-400 hover:text-sky-300 transition"
        >
          {{ t('tenantReg.alreadyHaveAccount') }}
        </router-link>

        <div class="flex items-center gap-2">
          <NButton
            attr-type="button"
            @click="clerkService.openNativeSignUp"
            class="font-semibold text-xs"
          >
            {{ t('tenantReg.clerkModalBtn') }}
          </NButton>

          <NButton
            type="primary"
            attr-type="submit"
            :loading="isSubmitting"
            class="font-bold text-xs shadow-lg shadow-sky-500/20"
          >
            <span>{{ t('tenantReg.continueToOrg') }}</span>
            <template #icon><ArrowRight class="w-4 h-4" /></template>
          </NButton>
        </div>
      </div>

      <div id="clerk-captcha" class="empty:hidden flex justify-center items-center my-2.5 w-full min-h-0"></div>
    </form>

    <!-- STEP 2: Baseball Organization / Self-Managed Tenant Details -->
    <form v-else @submit.prevent="handleRegisterTenant" class="space-y-4">
      <div class="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-2xl flex items-center gap-3">
        <div class="w-8 h-8 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
          <Building2 class="w-4 h-4" />
        </div>
        <p class="text-xs text-emerald-200">
          {{ t('tenantReg.orgTip') }}
        </p>
      </div>

      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.orgNameLabel') }} *</label>
        <NInput
          v-model:value="tenantForm.orgName"
          placeholder="例: 東京ガス硬式野球部 / 慶應義塾大学体育会野球部"
          size="medium"
          required
        />
      </div>

      <div>
        <label class="block text-xs font-semibold text-slate-300 mb-2">{{ t('tenantReg.tierLabel') }} *</label>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <button
            v-for="opt in tenantTierOptions"
            :key="opt.value"
            type="button"
            @click="tenantForm.tier = opt.value"
            :class="[
              'p-3 rounded-xl border text-left transition flex flex-col justify-between',
              tenantForm.tier === opt.value
                ? 'bg-sky-500/15 border-sky-500 text-sky-300 shadow-md shadow-sky-500/10'
                : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
            ]"
          >
            <div>
              <p class="text-xs font-bold text-slate-100">{{ opt.label }}</p>
              <p class="text-[10px] text-slate-500 mt-1 leading-tight">{{ opt.desc }}</p>
            </div>
            <div v-if="tenantForm.tier === opt.value" class="mt-2 text-sky-400 flex items-center gap-1 text-[10px] font-bold">
              <CheckCircle2 class="w-3.5 h-3.5" />
              {{ t('tenantReg.selectedBadge') }}
            </div>
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.sportTypeLabel') }}</label>
          <NInput
            v-model:value="tenantForm.sportType"
            placeholder="baseball"
            size="medium"
          />
        </div>
        <div>
          <label class="block text-xs font-semibold text-slate-300 mb-1.5">{{ t('tenantReg.seasonYearLabel') }}</label>
          <NInput
            v-model:value="tenantForm.seasonYear"
            placeholder="2025年 公式戦"
            size="medium"
          />
        </div>
      </div>

      <div class="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between text-xs">
        <span class="text-slate-400 flex items-center gap-1.5">
          <Globe2 class="w-3.5 h-3.5 text-sky-400" />
          {{ t('tenantReg.nativeScopingBadge') }}
        </span>
        <span class="text-[10px] font-bold text-emerald-400 font-mono">POST /api/v1/tenants/register</span>
      </div>

      <div class="pt-3 flex items-center justify-between">
        <button
          type="button"
          @click="currentStep = 1"
          class="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
        >
          <ArrowLeft class="w-3.5 h-3.5" />
          <span>{{ t('tenantReg.backToManager') }}</span>
        </button>

        <NButton
          type="primary"
          attr-type="submit"
          :loading="isSubmitting"
          class="font-bold text-xs shadow-lg shadow-sky-500/20"
        >
          <template #icon><ShieldCheck class="w-4 h-4" /></template>
          <span>{{ t('tenantReg.createAndLaunch') }}</span>
        </NButton>
      </div>

      <div id="clerk-captcha" class="empty:hidden flex justify-center items-center my-2.5 w-full min-h-0"></div>
    </form>
  </div>
</template>
